import { useState, useEffect, useMemo, useCallback, type CSSProperties } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
// ============================================================================
// External Declarations
// ============================================================================

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { getOfficerLabel } from '@/utils/legacy-utils';

// ============================================================================
// ActivityReportManagementScreen Component
// ============================================================================

export function ActivityReportManagementScreen() {
  const { theme } = useTheme();

  // --- Labels ---
  const officerLabel = useMemo(() => getOfficerLabel(), []);

  // --- Settings State ---
  const [areCallCardOfficerTimesOnPdf, setAreCallCardOfficerTimesOnPdf] = useState<boolean | null>(null);
  const [areCallCommentsOnPdf, setAreCallCommentsOnPdf] = useState<boolean | null>(null);
  const [areAttachmentsOnPdf, setAreAttachmentsOnPdf] = useState<boolean | null>(null);
  const [isPriorityOnPdf, setIsPriorityOnPdf] = useState<boolean | null>(null);
  const [areTasksOnPdf, setAreTasksOnPdf] = useState<boolean | null>(null);
  const [taskContentMode, setTaskContentMode] = useState<string | null>(null);
  const [callCardsHeader, setCallCardsHeader] = useState<string | null>(null);
  const [caseReportsHeader, setCaseReportsHeader] = useState<string | null>(null);
  const [notesHeader, setNotesHeader] = useState<string | null>(null);
  const [tourStopsHeader, setTourStopsHeader] = useState<string | null>(null);

  // --- Computed ---
  const settingsLoaded = useMemo(() => {
    return areCallCardOfficerTimesOnPdf !== null &&
      areCallCommentsOnPdf !== null &&
      areAttachmentsOnPdf !== null &&
      isPriorityOnPdf !== null &&
      areTasksOnPdf !== null &&
      taskContentMode !== null &&
      callCardsHeader !== null &&
      caseReportsHeader !== null &&
      notesHeader !== null &&
      tourStopsHeader !== null;
  }, [areCallCardOfficerTimesOnPdf, areCallCommentsOnPdf, areAttachmentsOnPdf, isPriorityOnPdf, areTasksOnPdf, taskContentMode, callCardsHeader, caseReportsHeader, notesHeader, tourStopsHeader]);

  // --- Save Settings ---
  const updateSettings = useCallback(() => {
    const data = {
      areCallCardOfficerTimesOnPdf,
      areCallCommentsOnPdf,
      areAttachmentsOnPdf,
      isPriorityOnPdf,
      areTasksOnPdf,
      taskContentMode: taskContentMode || 'NAME',
      callCardsHeader,
      caseReportsHeader,
      notesHeader,
      tourStopsHeader,
    };
    api.updateActivityReportSettings(data, () => {
      App.showPopupNotification('Activity Report PDF settings saved.', 'success');
    }, () => {
      App.showPopupNotification('Error updating settings.', 'error');
    });
  }, [areCallCardOfficerTimesOnPdf, areCallCommentsOnPdf, areAttachmentsOnPdf, isPriorityOnPdf, areTasksOnPdf, taskContentMode, callCardsHeader, caseReportsHeader, notesHeader, tourStopsHeader]);

  // --- Initial Load ---
  useEffect(() => {
    api.getActivityReportSettings((result: any) => {
      setAreCallCardOfficerTimesOnPdf(result.areCallCardOfficerTimesOnPdf);
      setAreCallCommentsOnPdf(result.areCallCommentsOnPdf);
      setAreAttachmentsOnPdf(result.areAttachmentsOnPdf);
      setIsPriorityOnPdf(result.isPriorityOnPdf);
      setAreTasksOnPdf(result.areTasksOnPdf);
      setTaskContentMode(result.taskContentMode || 'NAME');
      setCallCardsHeader(result.callCardsHeader);
      setCaseReportsHeader(result.caseReportsHeader);
      setNotesHeader(result.notesHeader);
      setTourStopsHeader(result.tourStopsHeader);
    }, () => {
      App.showPopupNotification('Error getting settings.', 'error');
    });
  }, []);

  // --- Styles (kstc-radio-* classes are unstyled in this app; lay out the group explicitly) ---
  const radioLabelStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
  const radioInputStyle: CSSProperties = { margin: 0, flexShrink: 0 };

  // --- Render ---
  return (
    <div className={`activity-report-management-screen ${theme}`.trim()}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Activity Report PDF Settings</h3>
                <p className="kst-page-description">
                  Configure how Activity Reports are generated and what information is included in the PDF.
                </p>
              </div>

              {settingsLoaded ? (
                <div className="kst-settings-form">
                  <div className="kst-checkbox-group">
                    <input
                      type="checkbox"
                      id="chkAreCallCardOfficerTimesOnPdf"
                      className="kstc-checkbox"
                      checked={areCallCardOfficerTimesOnPdf || false}
                      onChange={(e) => setAreCallCardOfficerTimesOnPdf(e.target.checked)}
                    />
                    <label className="kstc-checkbox-label" htmlFor="chkAreCallCardOfficerTimesOnPdf">
                      Show {officerLabel.toLowerCase()} times (dispatch, enroute, on-scene, cleared)
                    </label>
                  </div>
                  <div className="kst-checkbox-group">
                    <input
                      type="checkbox"
                      id="chkAreCallCommentsOnPdf"
                      className="kstc-checkbox"
                      checked={areCallCommentsOnPdf || false}
                      onChange={(e) => setAreCallCommentsOnPdf(e.target.checked)}
                    />
                    <label className="kstc-checkbox-label" htmlFor="chkAreCallCommentsOnPdf">
                      Show call comments
                    </label>
                  </div>
                  <div className="kst-checkbox-group">
                    <input
                      type="checkbox"
                      id="chkAreAttachmentsOnPdf"
                      className="kstc-checkbox"
                      checked={areAttachmentsOnPdf || false}
                      onChange={(e) => setAreAttachmentsOnPdf(e.target.checked)}
                    />
                    <label className="kstc-checkbox-label" htmlFor="chkAreAttachmentsOnPdf">
                      Show attachments
                    </label>
                  </div>
                  <div className="kst-checkbox-group">
                    <input
                      type="checkbox"
                      id="chkIsPriorityOnPdf"
                      className="kstc-checkbox"
                      checked={isPriorityOnPdf || false}
                      onChange={(e) => setIsPriorityOnPdf(e.target.checked)}
                    />
                    <label className="kstc-checkbox-label" htmlFor="chkIsPriorityOnPdf">
                      Show priority and order by priority
                    </label>
                  </div>
                  <div className="kst-form-group">
                    <label className="kstc-label" style={{ display: 'block', marginBottom: '6px' }}>Tasks on tour stops</label>
                    <div className="kstc-radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label className="kstc-radio-label" style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="task_content_mode"
                          style={radioInputStyle}
                          checked={!areTasksOnPdf}
                          onChange={() => setAreTasksOnPdf(false)}
                        />
                        <span>Don't show tasks</span>
                      </label>
                      <label className="kstc-radio-label" style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="task_content_mode"
                          style={radioInputStyle}
                          checked={!!areTasksOnPdf && taskContentMode === 'NAME'}
                          onChange={() => { setAreTasksOnPdf(true); setTaskContentMode('NAME'); }}
                        />
                        <span>Task name only</span>
                      </label>
                      <label className="kstc-radio-label" style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="task_content_mode"
                          style={radioInputStyle}
                          checked={!!areTasksOnPdf && taskContentMode === 'DESCRIPTION'}
                          onChange={() => { setAreTasksOnPdf(true); setTaskContentMode('DESCRIPTION'); }}
                        />
                        <span>Task description only</span>
                      </label>
                      <label className="kstc-radio-label" style={radioLabelStyle}>
                        <input
                          type="radio"
                          name="task_content_mode"
                          style={radioInputStyle}
                          checked={!!areTasksOnPdf && taskContentMode === 'BOTH'}
                          onChange={() => { setAreTasksOnPdf(true); setTaskContentMode('BOTH'); }}
                        />
                        <span>Both name and description</span>
                      </label>
                    </div>
                  </div>

                  <div className="kst-form-group">
                    <label className="kstc-label" htmlFor="inpCallCardsHeader">Calls for Service Header</label>
                    <input
                      type="text"
                      id="inpCallCardsHeader"
                      className="kstc-input"
                      value={callCardsHeader || ''}
                      onChange={(e) => setCallCardsHeader(e.target.value)}
                    />
                  </div>
                  <div className="kst-form-group">
                    <label className="kstc-label" htmlFor="inpCaseReportsHeader">Case Reports Header</label>
                    <input
                      type="text"
                      id="inpCaseReportsHeader"
                      className="kstc-input"
                      value={caseReportsHeader || ''}
                      onChange={(e) => setCaseReportsHeader(e.target.value)}
                    />
                  </div>
                  <div className="kst-form-group">
                    <label className="kstc-label" htmlFor="inpNotesHeader">Notes Header</label>
                    <input
                      type="text"
                      id="inpNotesHeader"
                      className="kstc-input"
                      value={notesHeader || ''}
                      onChange={(e) => setNotesHeader(e.target.value)}
                    />
                  </div>
                  <div className="kst-form-group">
                    <label className="kstc-label" htmlFor="inpTourStopsHeader">Tour Stops Header</label>
                    <input
                      type="text"
                      id="inpTourStopsHeader"
                      className="kstc-input"
                      value={tourStopsHeader || ''}
                      onChange={(e) => setTourStopsHeader(e.target.value)}
                    />
                  </div>

                  <div className="kst-settings-actions">
                    <button type="button" className="kstc-btn kstc-btn--primary" onClick={updateSettings}>
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="kst-loading"></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

